import { useMemo } from 'react';
import { motion } from 'motion/react';
import { snappyTween } from './motionUi';

/**
 * @param {object} props
 * @param {string} props.profileDisplayName
 * @param {() => void} props.onOpenNickname
 * @param {Array<{ _id: string, name: string }>} props.kinksCatalog
 * @param {string[]} props.selectedKinkIds — Mongo _id как строки (как в userstate.kinks)
 * @param {(ids: string[]) => void} props.onKinksChange — сохранение на сервер делает родитель
 * @param {boolean} [props.kinksSaving]
 */
export default function GameProfileModal({
  profileDisplayName,
  onOpenNickname,
  kinksCatalog,
  selectedKinkIds,
  onKinksChange,
  kinksSaving = false,
  realItemsCatalog = [],
  selectedRealItemIds = [],
  onRealItemsChange,
  realItemsSaving = false
}) {
  const selected = useMemo(() => new Set((selectedKinkIds || []).map(String)), [selectedKinkIds]);
  const selectedReal = useMemo(() => new Set((selectedRealItemIds || []).map(String)), [selectedRealItemIds]);

  const toggle = (id) => {
    const s = String(id);
    const base = (selectedKinkIds || []).map(String);
    const next = selected.has(s) ? base.filter((x) => x !== s) : [...base, s];
    onKinksChange(next);
  };

  const toggleReal = (id) => {
    if (!onRealItemsChange) return;
    const s = String(id);
    const base = (selectedRealItemIds || []).map(String);
    const next = selectedReal.has(s) ? base.filter((x) => x !== s) : [...base, s];
    onRealItemsChange(next);
  };

  return (
    <motion.div
      className="space-y-6 p-5"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
    >
      <section className="space-y-3">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-game-muted">Profile</p>
        <div className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-game-surface/80 px-3 py-2.5">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] text-game-muted">Display name</p>
            <p className="truncate text-sm font-semibold text-game-text">
              {profileDisplayName?.trim() ? profileDisplayName : '—'}
            </p>
          </div>
          <motion.button
            type="button"
            transition={snappyTween}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={onOpenNickname}
            className="shrink-0 rounded-xl border border-white/15 bg-game-popup/40 px-3 py-2 text-xs font-semibold text-game-text hover:border-game-accent/35 hover:bg-game-accent/10"
          >
            Change
          </motion.button>
        </div>
      </section>

      <section className="space-y-3 border-t border-white/10 pt-5">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-game-muted">Kinks</p>
        {kinksCatalog.length === 0 ? (
          <p className="text-xs text-game-muted">No kinks in the catalog yet.</p>
        ) : (
          <ul className="vr-scroll-y max-h-[min(50vh,320px)] space-y-2 overflow-y-auto pr-1">
            {kinksCatalog.map((k) => {
              const id = String(k._id);
              return (
                <li
                  key={id}
                  className="flex cursor-pointer items-center gap-3 rounded-xl border border-white/10 bg-game-surface/60 px-3 py-2.5 transition-colors hover:border-white/20"
                >
                  <input
                    id={`game-profile-kink-${id}`}
                    type="checkbox"
                    checked={selected.has(id)}
                    disabled={kinksSaving}
                    onChange={() => toggle(id)}
                    className="h-4 w-4 shrink-0 rounded border-white/30 bg-game-surface text-game-accent focus:ring-game-accent/50 disabled:opacity-50"
                  />
                  <label
                    htmlFor={`game-profile-kink-${id}`}
                    className="min-w-0 flex-1 cursor-pointer text-sm leading-snug text-game-text"
                  >
                    {k.name || '—'}
                  </label>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="space-y-3 border-t border-white/10 pt-5">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-game-muted">Real Items</p>
        {realItemsCatalog.length === 0 ? (
          <p className="text-xs text-game-muted">No real items in the catalog yet.</p>
        ) : (
          <div className="vr-scroll-y max-h-[min(50vh,320px)] space-y-4 overflow-y-auto pr-1">
            {['Outfit', 'Bondage', 'Toys', 'Dolls', 'Other'].map((cat) => {
              const catItems = realItemsCatalog.filter((it) => (it.category || 'Other') === cat);
              if (catItems.length === 0) return null;
              return (
                <div key={cat}>
                  <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-game-accent/70">{cat}</p>
                  <ul className="space-y-2">
                    {catItems.map((it) => {
                      const id = String(it._id);
                      return (
                        <li
                          key={id}
                          className="flex cursor-pointer items-center gap-3 rounded-xl border border-white/10 bg-game-surface/60 px-3 py-2.5 transition-colors hover:border-white/20"
                        >
                          <input
                            id={`game-profile-realitem-${id}`}
                            type="checkbox"
                            checked={selectedReal.has(id)}
                            disabled={realItemsSaving}
                            onChange={() => toggleReal(id)}
                            className="h-4 w-4 shrink-0 rounded border-white/30 bg-game-surface text-game-accent focus:ring-game-accent/50 disabled:opacity-50"
                          />
                          <label
                            htmlFor={`game-profile-realitem-${id}`}
                            className="min-w-0 flex-1 cursor-pointer text-sm leading-snug text-game-text"
                          >
                            {it.name || '—'}
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </motion.div>
  );
}
